import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Box, Alert, CircularProgress } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dashboardService } from '../../services/dashboardService'
import { UserService } from '../../services/userService'
import {
  PersonalStats,
  RecentTestResults,
  UpcomingDeadlines,
  AnnouncementCard,
} from '../../components/dashboard'
import { TutorialModal, useMyEnrollmentsTutorialSteps } from '../../components/tutorial'
import { DashboardStats } from '../../types/dashboard'

export const UserDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const { t: tTutorial } = useTranslation('tutorial')
  const { user } = useAuth()
  const navigate = useNavigate()
  const [userStats, setUserStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // チュートリアル関連の状態
  const [showTutorial, setShowTutorial] = useState(false)
  const [tutorialChecked, setTutorialChecked] = useState(false)
  const tutorialSteps = useMyEnrollmentsTutorialSteps()

  useEffect(() => {
    let isMounted = true

    const loadDashboardData = async () => {
      if (!user) return

      try {
        setLoading(true)
        setError('')
        const userData = await dashboardService.getUserDashboardStats()
        if (isMounted) setUserStats(userData)
      } catch (err) {
        console.error('Dashboard data loading failed:', err)
        if (isMounted) setError(t('loadError'))
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadDashboardData()

    return () => {
      isMounted = false
    }
  }, [user, t])

  // チュートリアル状況確認（USERロールのみ、初回のみ）
  useEffect(() => {
    const checkTutorial = async () => {
      // USERロールのみチェック
      if (!user || user.role !== 'USER' || tutorialChecked) {
        return
      }

      try {
        const { tutorialCompleted } = await UserService.getTutorialStatus()
        // myEnrollmentsチュートリアルが未完了の場合、自動表示
        if (!tutorialCompleted?.myEnrollments) {
          setShowTutorial(true)
        }
      } catch (err) {
        console.error('チュートリアル状況取得エラー:', err)
      } finally {
        setTutorialChecked(true)
      }
    }

    checkTutorial()
  }, [user, tutorialChecked])

  // チュートリアル完了処理
  const handleTutorialComplete = async () => {
    try {
      await UserService.completeTutorial('myEnrollments')
      setShowTutorial(false)
    } catch (err) {
      console.error('チュートリアル完了マークエラー:', err)
      setShowTutorial(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      {/* チュートリアルモーダル（ログイン直後に表示） */}
      <TutorialModal
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
        title={tTutorial('myEnrollments.title')}
        steps={tutorialSteps}
      />

      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
      >
        {t('title')}
      </Typography>

      <Typography variant="body1" color="textSecondary" gutterBottom>
        {t('welcome', { name: user?.name })}
      </Typography>

      {/* 2カラムレイアウト: メインコンテンツ左、お知らせ右 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        {/* メインコンテンツ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {userStats && (
            <>
              <PersonalStats
                stats={userStats}
                onEnrollmentsClick={() => navigate('/my-enrollments')}
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: { xs: 2, sm: 3 },
                }}
              >
                <RecentTestResults results={userStats.recentTestResults} />
                <UpcomingDeadlines
                  deadlines={userStats.upcomingDeadlines}
                  onViewAll={() => navigate('/my-enrollments')}
                />
              </Box>
            </>
          )}
        </Box>

        {/* 右サイドバー: お知らせ */}
        <Box
          sx={{
            width: { xs: '100%', lg: 280 },
            flexShrink: 0,
          }}
        >
          <AnnouncementCard
            userRole={user?.role as 'CORPORATE_ADMIN' | 'USER' | 'SUPER_ADMIN'}
            variant="sidebar"
          />
        </Box>
      </Box>
    </Box>
  )
}
